import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Alert, Button } from "@repo/ui";
import AnimatedCard from "../../../../components/AnimatedCard/AnimatedCard";
import PageContainer from "../../../../components/PageContainer/PageContainer";
import StepIndicator from "../../../../components/StepIndicator/StepIndicator";
import BackButton from "../../../../components/BackButton";
import withAuth from "../../../../hoc/withAuth/withAuth";
import { getWithdrawSteps } from "../../constants/steps";
import { withTranslations } from "../../../../hoc/withTranslations/withTranslations";
import {
  getManualProfile,
  ManualProfileResponse,
} from "../../../../services/withdrawApiService";

const LOCALE = "modules_MyGiftTable_pages_WithdrawPage_WithdrawPageStep3Manual";

const WithdrawPageStep3Manual: React.FC = () => {
  const { t } = useTranslation(LOCALE);
  const steps = getWithdrawSteps(t);
  const navigate = useNavigate();
  const [profile, setProfile] = useState<ManualProfileResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Get withdrawID from localStorage (stored after creation).
  const storedWithdrawal = localStorage.getItem("withdrawal");
  const withdrawID = storedWithdrawal
    ? (JSON.parse(storedWithdrawal).withdrawalID as string | undefined)
    : undefined;

  useEffect(() => {
    const fetchProfile = async () => {
      if (!withdrawID) {
        setError(t("missingWithdrawId", "No se encontró el retiro activo."));
        setLoading(false);
        return;
      }
      try {
        const result = await getManualProfile(withdrawID);
        setProfile(result);
      } catch (err) {
        console.error("Error fetching manual profile", err);
        // Leave profile as null; error message already logged.
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [withdrawID, t]);

  const handleBack = () => {
    navigate("/my-table/withdraw/step2");
  };

  const handleContinue = () => {
    navigate("/my-table/withdraw/step4-manual");
  };

  const handleReview = () => {
    navigate("/my-table/withdraw/step5-manual");
  };

  return (
    <PageContainer>
      <div className="mt-16" />
      <StepIndicator steps={steps} currentStep={3} progressPercentage={75} />
      <AnimatedCard className="bg-white max-w-lg w-full">
        <BackButton text={t("backButton", "Back")} onClick={handleBack} />
        {loading ? (
          <div className="text-gray-600">{t("loading", "Cargando...")}</div>
        ) : error ? (
          <Alert message={error} level="error" />
        ) : profile ? (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold mb-2 text-primary">
              {t("reviewHeading", "Revisa tus datos para continuar")}
            </h2>
            <p className="text-gray-600">
              {t(
                "reviewIntro",
                "Ya tenemos tu información para procesar tu retiro. Puedes confirmarla o actualizarla."
              )}
            </p>
            <div className="bg-background p-4 rounded space-y-2 text-sm text-gray-700">
              {profile.bank && (
                <div>
                  <span className="font-semibold">{t("bankLabel", "Banco")}:</span>{" "}
                  {profile.bank} {profile.account && `• ${profile.account}`}
                </div>
              )}
              {profile.holderName && (
                <div>
                  <span className="font-semibold">
                    {t("holderLabel", "Titular/Representante")}:
                  </span>{" "}
                  {profile.holderName} {profile.rfc && `• ${profile.rfc}`}
                </div>
              )}
              {profile.contactEmail && (
                <div>
                  <span className="font-semibold">{t("contactLabel", "Contacto")}:</span>{" "}
                  {profile.contactEmail} {profile.contactPhone && `• ${profile.contactPhone}`}
                </div>
              )}
              {profile.addressLine1 && (
                <div>
                  <span className="font-semibold">{t("addressLabel", "Dirección")}:</span>{" "}
                  {profile.addressLine1}
                  {profile.addressLine2 && `, ${profile.addressLine2}`},{" "}
                  {profile.city}, {profile.state}, {profile.postalCode},{" "}
                  {profile.country}
                </div>
              )}
            </div>
            <div className="flex flex-col gap-3">
              <Button
                label={t("confirmButton", "Confirmar y continuar")}
                onClick={handleReview}
                type="primary"
                className="w-full"
              />
              <Button
                label={t("editButton", "Editar mis datos")}
                onClick={handleContinue}
                type="secondary"
                className="w-full"
              />
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold mb-4 text-primary">
              {t("manualHeading", "Confirma tus datos para recibir tu retiro")}
            </h2>
            <p className="text-gray-600 mb-4">
              {t(
                "manualIntro",
                "Te ayudaremos a completar tu retiro. Solo necesitamos algunos datos para validar tu cuenta y procesar el pago."
              )}
            </p>
            <Alert
              message={
                <div className="space-y-2">
                  <p className="font-semibold">
                    {t("weWillNeed", "Información requerida:")}
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                    <li>{t("bank", "Cuenta bancaria para recibir tu dinero")}</li>
                    <li>
                      {t(
                        "repInfo",
                        "Titular o representante: nombre, correo, teléfono, fecha de nacimiento, RFC"
                      )}
                    </li>
                    <li>
                      {t(
                        "address",
                        "Dirección: domicilio, depto/unidad (opcional), ciudad, estado, CP, país"
                      )}
                    </li>
                  </ul>
                </div>
              }
              level="info"
            />
            <div className="mt-6 space-y-3">
              <p className="text-gray-700 text-sm">
                {t(
                  "manualNextSteps",
                  "En el siguiente paso te pediremos estos datos para terminar tu solicitud."
                )}
              </p>
              <Button
                label={t("continueButton", "Continuar")}
                onClick={handleContinue}
                type="primary"
                className="w-full"
              />
            </div>
          </div>
        )}
      </AnimatedCard>
    </PageContainer>
  );
};

export default withAuth(withTranslations(LOCALE)(WithdrawPageStep3Manual));
